import React, { useCallback, useState } from 'react';
import { TouchableOpacity } from 'react-native';

import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { useDebugSimulateLiveMarketsUpdates } from '@/feature/betslip-pickem/hooks/use-betslip-debug-hooks';
import { usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { useLocalFlagsStore } from '@/feature/devsettings/hooks/use-dev-settings';
import { PlayerProfileEventWidget } from '@/feature/player-profile-sheets/components/PlayerProfileEventWidget';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { RootStackParamList } from '@/navigation/types';
import { MarketStatus } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isUFC, nflFullSeason } from '@/utils/league';

export const PlayerProfileEventSection = ({
    playerInfo,
    closeSinglePlayerProfile,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    closeSinglePlayerProfile?: () => void;
}) => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const event = playerInfo?.event;
    //We dont show the event widget for NFL Full Season, as there should be no events for it
    const showEventWidget = !nflFullSeason(playerInfo.league);

    const { bottomSheetCircularPagerRef } = useFullScreenPagerTransition();

    const replaceWithEventSheet = useCallback(() => {
        useEventsTabsStore.getState().actions.setSelectedTab(0);
        navigation.replace('FantasyEventsSheets', {
            league: event?.league,
            eventId: event?.id || '',
            contextKey: Date.now().toString(),
        });
    }, [navigation, event?.league, event?.id]);

    const onPressEventWidget = useCallback(() => {
        if (closeSinglePlayerProfile) {
            closeSinglePlayerProfile();
            return;
        }

        if (isWeb) {
            replaceWithEventSheet();
            return;
        }

        // close() slides the sheet down, then the sheet calls goBack
        // we block that goBack and replace the profile sheet with the event sheet
        const unsubscribe = navigation.addListener('beforeRemove', removeEvent => {
            unsubscribe();
            removeEvent.preventDefault();
            replaceWithEventSheet();
        });
        bottomSheetCircularPagerRef.current?.close();
    }, [closeSinglePlayerProfile, replaceWithEventSheet, navigation, bottomSheetCircularPagerRef]);

    return (
        <>
            {showEventWidget ? (
                <Box px={'s16'} zIndex={1}>
                    {/**Disable clicking for UFC because there is no "Event Page" for UFC */}
                    <TouchableOpacity onPress={onPressEventWidget} disabled={isUFC(playerInfo.league)}>
                        <PlayerProfileEventWidget event={playerInfo?.event} player={playerInfo} />
                    </TouchableOpacity>
                    <DebugButtons playerId={playerInfo.id} eventId={playerInfo.event.id} />
                </Box>
            ) : null}
        </>
    );
};

const DebugButtons = ({ playerId, eventId }: { playerId: string; eventId: string }) => {
    const { localFlags } = useLocalFlagsStore();
    const [debugSimulateLiveMarkets, setDebugSimulateLiveMarkets] = useState(false);
    const [enableRandomMarketSuspension, setEnableRandomMarketSuspension] = useState(false);
    const [marketStatus, setMarketStatus] = useState(MarketStatus.Opened);

    useDebugSimulateLiveMarketsUpdates({
        eventId,
        playerId,
        changeAllMarkets: false,
        enabled: debugSimulateLiveMarkets,
        enableRandomMarketSuspension: enableRandomMarketSuspension,
    });

    return localFlags?.playerProfileDebugButtons?.enabled ? (
        <Column gap={'s2'}>
            <TouchableOpacity
                onPress={() => {
                    Clipboard.setString(playerId);
                }}
            >
                <Text>{playerId}</Text>
            </TouchableOpacity>

            <Button
                label={'Simulate LIVE websockets markets'}
                onPress={() => {
                    setDebugSimulateLiveMarkets(true);
                }}
            />
            <Button
                label={'Simulate LIVE market suspension'}
                onPress={() => {
                    setEnableRandomMarketSuspension(true);
                }}
            />
            <Button
                label={'Add current values'}
                onPress={() => {
                    usePlayerPropsStore.getState().actions._debugEnableCurrentValues(playerId, eventId);
                }}
            />
            <Button
                label={'Suspend/Resume all markets'}
                onPress={() => {
                    usePlayerPropsStore
                        .getState()
                        .actions._debugChangePlayerMarketsStatus(playerId, eventId, 'all', marketStatus);
                    setMarketStatus(
                        marketStatus === MarketStatus.Opened ? MarketStatus.Suspended : MarketStatus.Opened
                    );
                }}
            />
            <Button
                label={'Log current markets'}
                onPress={() => {
                    const playerProps = usePlayerPropsStore.getState()._playerMarkets;
                    const key = `${eventId}-${playerId}`;
                    console.log('Current markets for player', key, playerProps[key]);
                }}
            />
            <Button
                label={'Invalidate player market IDs'}
                onPress={() => {
                    const allPlayersMarkets = usePlayerPropsStore.getState()._playerMarkets;
                    const updateMarkets = usePlayerPropsStore.getState().actions.updateStorePlayerProjections;
                    const key = `${eventId}-${playerId}`;
                    const playerMarkets = allPlayersMarkets[key];
                    const invalidatedMarkets = playerMarkets?.map(market => ({
                        ...market,
                        marketId: market.marketId + 'bustedId',
                        allowedOptions: market.allowedOptions?.map(option => ({
                            ...option,
                            marketOptionId: option.marketOptionId + 'busted',
                        })),
                    }));
                    updateMarkets(playerId, eventId, invalidatedMarkets || []);
                }}
            />
        </Column>
    ) : null;
};
